import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Check, Trash2, Mail, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ContactManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread, read

  const load = async () => {
    try {
      const res = await api.get("/contact/messages");
      setMessages(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/contact/messages/${id}/read`);
      toast.success("Message marque comme lu");
      load();
    } catch { toast.error("Erreur"); }
  };

  const deleteMsg = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await api.delete(`/contact/messages/${id}`);
      toast.success("Message supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const filtered = messages.filter(m => {
    if (filter === "unread") return !m.read;
    if (filter === "read") return m.read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div data-testid="contact-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">Messages</h1>
          <p className="text-muted-foreground mt-1">
            {unreadCount > 0 ? `${unreadCount} message${unreadCount > 1 ? "s" : ""} non lu${unreadCount > 1 ? "s" : ""}` : "Tous les messages sont lus"}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6" data-testid="message-filters">
        {[
          { key: "all", label: "Tous", count: messages.length },
          { key: "unread", label: "Non lus", count: unreadCount },
          { key: "read", label: "Lus", count: messages.length - unreadCount },
        ].map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? "default" : "outline"}
            size="sm"
            className="rounded-none"
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
          >
            {f.label} ({f.count})
          </Button>
        ))}
      </div>

      {/* Messages List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="border rounded-none animate-pulse">
              <CardContent className="p-6"><div className="h-20 bg-muted rounded" /></CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border rounded-none">
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Aucun message</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="messages-list">
          {filtered.map((msg) => (
            <Card key={msg.id} className={`border rounded-none transition-colors ${!msg.read ? "border-l-2 border-l-rose-500 bg-rose-50/30" : ""}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold">{msg.name}</h3>
                      {!msg.read && <Badge className="rounded-none bg-rose-500 text-white text-[10px] px-1.5 py-0">Nouveau</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {msg.email}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {msg.created_at ? new Date(msg.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                    {msg.subject && <p className="text-sm font-medium mb-1">{msg.subject}</p>}
                    <p className="text-sm text-muted-foreground leading-relaxed">{msg.message}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {!msg.read && (
                      <Button variant="ghost" size="icon" onClick={() => markRead(msg.id)} title="Marquer comme lu" data-testid={`mark-read-${msg.id}`}>
                        <Check className="h-4 w-4 text-emerald-600" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => deleteMsg(msg.id)} title="Supprimer" data-testid={`delete-msg-${msg.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
